import React from "react"
import { Box, ButtonBase, Stack } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { colors, typeScale, typography } from "../theme/tokens"
import { VIRA_CARD_RADIUS } from "../atoms/ViraCard"

export type ContentTabOption<T extends string = string> = {
  value: T
  label: string
  disabled?: boolean
}

export type ContentTabsProps<T extends string = string> = {
  options: ContentTabOption<T>[]
  value: T
  onChange?: (value: T) => void
  /** Accessible name for the tab list */
  "aria-label"?: string
  fullWidth?: boolean
}

/**
 * Card-chrome tab strip for Client Detail panes (Scores / Transcript / …).
 * Matches ContentCard radius, border, and typeScale — not MUI Button defaults.
 */
export function ContentTabs<T extends string>({
  options,
  value,
  onChange,
  "aria-label": ariaLabel = "Section tabs",
  fullWidth = true,
}: ContentTabsProps<T>) {
  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={(theme) => {
        const border = theme.palette.vira?.border ?? colors.vira.border
        const soft = theme.palette.vira?.softSurface ?? colors.vira.softSurface
        return {
          display: "inline-flex",
          width: fullWidth ? "100%" : "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
          p: 0.5,
          gap: 0.5,
          borderRadius: VIRA_CARD_RADIUS,
          border: `1px solid ${border}`,
          backgroundColor: soft,
          // Avoid overflowX:auto — horizontally scrollable regions steal the
          // first vertical trackpad gesture on macOS.
          overflowX: "clip",
          overflowY: "hidden",
        }
      }}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <ButtonBase
            key={option.value}
            role="tab"
            aria-selected={selected}
            disabled={option.disabled}
            onClick={() => onChange?.(option.value)}
            sx={composeSx((theme) => {
              const ink = theme.palette.vira?.ink ?? colors.vira.ink
              const muted = theme.palette.vira?.muted ?? colors.vira.muted
              const activeBg =
                theme.palette.vira?.navActiveBg ?? colors.vira.navActiveBg
              const paper =
                theme.palette.background?.paper ?? colors.neutral.white
              return {
                flex: fullWidth ? "1 1 0" : "0 0 auto",
                minWidth: 0,
                minHeight: 40,
                px: 2,
                py: 1,
                borderRadius: "8px",
                backgroundColor: selected ? paper : "transparent",
                boxShadow: selected
                  ? `inset 0 0 0 1px ${theme.palette.vira?.border ?? colors.vira.border}`
                  : "none",
                color: selected ? ink : muted,
                opacity: option.disabled ? 0.45 : 1,
                "&:hover": {
                  backgroundColor: selected ? paper : activeBg,
                },
              }
            })}
          >
            <Stack
              component="span"
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: "100%",
                fontFamily: typography.fontFamily.display,
                fontSize: typeScale.body,
                fontWeight: selected ? 600 : 500,
                letterSpacing: "-0.01em",
                lineHeight: 1.3,
                textTransform: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {option.label}
            </Stack>
          </ButtonBase>
        )
      })}
    </Box>
  )
}
